import { useState } from "react";
import { Loader2, Eye, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const WrapPreview = ({ bookId, trimKey }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = () => {
    setOpen(true);
    setLoading(true);
    setData(null);
    api.wrapPreview(bookId, trimKey)
      .then(setData)
      .catch((e) => setData({ image: null, issues: [e?.response?.data?.detail || "Preview failed"] }))
      .finally(() => setLoading(false));
  };

  return (
    <>
      <button
        data-testid="preview-wrap-btn"
        onClick={load}
        className="flex items-center gap-2 font-ui text-sm font-medium bg-navy-800 hover:bg-navy-700 text-slate-100 border border-gold-400/30 rounded-lg px-4 py-2 transition-colors"
      >
        <Eye className="w-4 h-4" /> Preview full wrap
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl bg-navy-900 border-gold-500/20" data-testid="wrap-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Full-wrap cover preview</DialogTitle>
            <DialogDescription className="font-read text-sm text-slate-400">
              Back · spine · front. Red = trim, blue = spine folds, yellow = KDP barcode area.
            </DialogDescription>
          </DialogHeader>
          {loading && (
            <div data-testid="wrap-preview-loading" className="flex items-center gap-2 py-16 justify-center text-slate-400 font-ui text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Rendering the wrap at this trim…
            </div>
          )}
          {data?.image && (
            <img data-testid="wrap-preview-image" src={data.image} alt="Full-wrap cover preview" className="w-full rounded-md border border-white/10" />
          )}
          {data && (
            <div className="space-y-2 font-ui text-xs text-slate-300">
              {data.size_in && (
                <p data-testid="wrap-preview-dims">
                  {data.pages} pages · spine {data.spine_in}" · full wrap {data.size_in[0]}" × {data.size_in[1]}" ·{" "}
                  {data.spine_text ? "spine text on" : "no spine text (KDP requires 80+ pages)"}
                </p>
              )}
              {data.issues?.length ? (
                <ul data-testid="wrap-preview-issues" className="space-y-1">
                  {data.issues.map((i) => (
                    <li key={i} className="flex gap-2 text-amber-300"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{i}</li>
                  ))}
                </ul>
              ) : (
                <p data-testid="wrap-preview-pass" className="flex items-center gap-2 text-emerald-300">
                  <CheckCircle2 className="w-3.5 h-3.5" /> KDP preflight passed: size, bleed, safe zones, spine and barcode area
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
